import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import pixydocsHorizDark from '@/assets/pixydocs-horiz-dark.svg'
import { showErrorSnackbar } from '@/utils/snackbars'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import PasswordStrengthCheck from './PasswordStrengthCheck'

type FormValues = {
  password: string
}

export default function ForgotPassword() {
  const stytch = useStytchB2BClient()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)
  const [isStrong, setIsStrong] = useState(false)

  const methods = useForm<FormValues>({
    defaultValues: {
      password: '',
    },
  })

  const {
    formState: { isDirty, isValid },
    watch,
  } = methods

  const currentPassword = watch('password')

  const handleOnSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const response = await stytch.passwords.discovery.resetByEmail({
          password_reset_token: searchParams.get('token') || '',
          password: values.password,
        })
        navigate(`/authenticate?token=${response.intermediate_session_token}`, {
          replace: true,
        })
      } catch {
        showErrorSnackbar(
          'An error occurred while resetting your password. Please try again.',
        )
      }
    },
    [navigate, searchParams, stytch.passwords.discovery],
  )

  return (
    <Stack spacing={6} alignItems="center">
      <img
        src={pixydocsHorizDark}
        alt="Pixydocs Logo"
        style={{ width: 'auto', height: '50px' }}
      />
      <PixyDocsForm
        methods={methods}
        onSubmit={(data) => {
          handleOnSubmit(data)
        }}
        style={{ width: '100%' }}
      >
        <Stack spacing={2}>
          <Typography variant="body1">Choose a new password</Typography>
          <FormTextField
            autoComplete="off"
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            label="Password"
            required
            useControllerProps={{
              rules: {
                validate: (value) => isStrong && value.trim().length > 0,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="inherit" />
                      ) : (
                        <Visibility fontSize="inherit" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {currentPassword && (
            <PasswordStrengthCheck
              password={currentPassword}
              setIsStrong={setIsStrong}
            />
          )}
          <Button type="submit" disabled={!isValid || !isDirty}>
            Continue
          </Button>
        </Stack>
      </PixyDocsForm>
    </Stack>
  )
}
